import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  Button,
  Input,
  Select,
  Textarea,
  Badge,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import { apiClient } from '../../shared/api/client';
import { formatCurrency } from '../../shared/utils/format';
import { CountHistory } from './CountHistory';
import {
  useWarehouses,
  useStockBalance,
  useCreateCount,
  isWarehouseActive,
  type CountItemInput,
} from './useInventory';

/**
 * Inventário físico (contagem cíclica).
 *
 * A tela anterior tratava a contagem como um registro por produto e chamava
 * `GET /inventory/counts`, rota que não existe. `CreateCountDto` é uma contagem
 * **de armazém**: warehouseId e uma lista de itens com quantidade
 * esperada, quantidade contada e custo unitário.
 *
 * O "esperado" e o custo saem de `GET /inventory/balance`, que é o saldo que o
 * sistema tem hoje. O histórico abaixo lista as contagens registradas.
 */
export function CountsPage(): JSX.Element {
  const { addToast } = useToast();
  const [warehouseId, setWarehouseId] = useState('');
  const [notes, setNotes] = useState('');
  const [contado, setContado] = useState<Record<string, string>>({});
  const [busca, setBusca] = useState('');

  const { data: warehouses, isLoading: carregandoArmazens } = useWarehouses();
  const { data: balances, isLoading: carregandoSaldo, error } = useStockBalance();
  const createCount = useCreateCount();

  const { data: produtos } = useQuery({
    queryKey: ['products', 'nomes'],
    queryFn: async () => apiClient.get<{ id: string; name: string }[]>('/products'),
    staleTime: 5 * 60 * 1000,
  });

  const nomePorProduto = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const p of produtos ?? []) mapa.set(p.id, p.name);
    return mapa;
  }, [produtos]);

  const armazensAtivos = useMemo(
    () => (warehouses ?? []).filter(isWarehouseActive),
    [warehouses],
  );

  const armazem = armazensAtivos.find((w) => w.id === warehouseId);

  /* Abre no primeiro armazem ativo em vez de no estado vazio — a contagem e
     por armazem e quase sempre ha um so. */
  useEffect(() => {
    if (warehouseId) return;
    const primeiro = armazensAtivos[0];
    if (primeiro) setWarehouseId(primeiro.id);
  }, [armazensAtivos, warehouseId]);

  /** Linhas do armazém escolhido, com o saldo que o sistema espera. */
  const linhas = useMemo(() => {
    if (!warehouseId) return [];
    const termo = busca.trim().toLowerCase();
    return (balances ?? [])
      .filter((b) => b.warehouseId === warehouseId)
      .map((b) => ({
        ...b,
        nome: nomePorProduto.get(b.productId) ?? b.productId,
      }))
      .filter((b) => (termo ? b.nome.toLowerCase().includes(termo) : true))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [balances, warehouseId, busca, nomePorProduto]);

  /** Só entra na contagem o que foi efetivamente digitado. */
  const itensContados = useMemo<CountItemInput[]>(() => {
    const itens: CountItemInput[] = [];
    for (const linha of linhas) {
      const bruto = contado[linha.id];
      if (bruto === undefined || bruto.trim() === '') continue;
      const quantidade = Number(bruto.replace(',', '.'));
      if (!Number.isFinite(quantidade) || quantidade < 0) continue;
      itens.push({
        productId: linha.productId,
        expectedQuantity: linha.onHand,
        countedQuantity: quantidade,
        unitCost: linha.unitCost,
        ...(linha.lotNumber ? { lotNumber: linha.lotNumber } : {}),
        ...(linha.serialNumber ? { serialNumber: linha.serialNumber } : {}),
      });
    }
    return itens;
  }, [linhas, contado]);

  const divergentes = itensContados.filter((i) => i.countedQuantity !== i.expectedQuantity);
  const impacto = divergentes.reduce(
    (soma, i) => soma + (i.countedQuantity - i.expectedQuantity) * i.unitCost,
    0,
  );

  const registrar = async (): Promise<void> => {
    if (!armazem) {
      addToast('Escolha o armazém da contagem.', 'error');
      return;
    }
    if (itensContados.length === 0) {
      addToast('Informe a quantidade contada de ao menos um produto.', 'error');
      return;
    }
    try {
      await createCount.mutateAsync({
        warehouseId: armazem.id,
        items: itensContados,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      addToast(
        `Contagem registrada: ${itensContados.length} item(ns), ${divergentes.length} com divergência.`,
        'success',
      );
      setContado({});
      setNotes('');
    } catch (err) {
      addToast((err as Error).message ?? 'Erro ao registrar contagem.', 'error');
    }
  };

  const carregando = carregandoArmazens || carregandoSaldo;

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Inventário Físico</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Contagem cíclica por armazém: confira o saldo do sistema e registre o
            que foi contado.
          </p>
        </div>
        <Button
          variant="primary"
          loading={createCount.isPending}
          disabled={itensContados.length === 0}
          onClick={() => void registrar()}
        >
          Registrar contagem
        </Button>
      </div>

      <Card>
        <CardBody>
          <div className="cnt-filters">
            <Select
              label="Armazém"
              value={warehouseId}
              onChange={(e) => {
                setWarehouseId(e.target.value);
                setContado({});
              }}
            >
              <option value="">Selecione…</option>
              {armazensAtivos.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} — {w.name}
                </option>
              ))}
            </Select>
            <Input
              label="Buscar produto"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Nome do produto"
              disabled={!warehouseId}
            />
          </div>

          {/* Histórico persistido em `GET /inventory/counts`. */}
          <CountHistory warehouseId={warehouseId} />
        </CardBody>
      </Card>

      <div className="cnt-gap" />

      <Card>
        <CardBody>
          {carregando ? (
            <LoadingSkeleton count={6} height="40px" />
          ) : error ? (
            <div className="cnt-state">
              <Badge variant="error" size="sm">
                erro
              </Badge>
              <p>Não foi possível carregar o saldo: {(error as Error).message}</p>
            </div>
          ) : !warehouseId ? (
            <p className="cnt-muted">Escolha um armazém para listar o saldo a conferir.</p>
          ) : linhas.length === 0 ? (
            <p className="cnt-muted">
              {busca
                ? 'Nenhum produto com esse nome neste armazém.'
                : 'Este armazém não tem saldo registrado.'}
            </p>
          ) : (
            <>
              <div className="cnt-summary">
                <span>
                  <strong>{itensContados.length}</strong> de {linhas.length} contado
                  {itensContados.length === 1 ? '' : 's'}
                </span>
                <span>
                  <strong>{divergentes.length}</strong> com divergência
                </span>
                <span className={impacto === 0 ? 'cnt-flat' : impacto > 0 ? 'cnt-up' : 'cnt-down'}>
                  Impacto no estoque: {impacto > 0 ? '+' : ''}
                  {formatCurrency(impacto)}
                </span>
              </div>

              <div className="cnt-table-wrap">
                <table className="cnt-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Lote / Série</th>
                      <th className="cnt-num">Esperado</th>
                      <th className="cnt-num">Contado</th>
                      <th className="cnt-num">Divergência</th>
                      <th className="cnt-num">Custo unit.</th>
                      <th className="cnt-num">Impacto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((linha) => {
                      const bruto = contado[linha.id] ?? '';
                      const quantidade = bruto.trim() === '' ? null : Number(bruto.replace(',', '.'));
                      const diff =
                        quantidade !== null && Number.isFinite(quantidade)
                          ? quantidade - linha.onHand
                          : null;
                      return (
                        <tr key={linha.id}>
                          <td>{linha.nome}</td>
                          <td className="cnt-mono">
                            {linha.lotNumber ?? linha.serialNumber ?? '—'}
                          </td>
                          <td className="cnt-num">{linha.onHand}</td>
                          <td className="cnt-num">
                            <Input
                              aria-label={`Quantidade contada de ${linha.nome}`}
                              className="cnt-qty"
                              type="number"
                              min={0}
                              step="0.0001"
                              placeholder="—"
                              value={bruto}
                              onChange={(e) =>
                                setContado((atual) => ({ ...atual, [linha.id]: e.target.value }))
                              }
                            />
                          </td>
                          <td className="cnt-num">
                            {diff === null ? (
                              <span className="cnt-muted">—</span>
                            ) : diff === 0 ? (
                              <span className="cnt-flat">0</span>
                            ) : (
                              <Badge variant={diff > 0 ? 'info' : 'warning'} size="sm">
                                {diff > 0 ? `+${diff}` : String(diff)}
                              </Badge>
                            )}
                          </td>
                          <td className="cnt-num">{formatCurrency(linha.unitCost)}</td>
                          <td className="cnt-num">
                            {diff === null || diff === 0
                              ? '—'
                              : formatCurrency(diff * linha.unitCost)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="cnt-notes">
                <Textarea
                  label="Observações da contagem (opcional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Ex.: contagem do turno da manhã, conferida por dois operadores"
                />
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <CountHistory warehouseId={warehouseId} />

      <style>{`
        .cnt-filters { display: grid; grid-template-columns: minmax(220px, 320px) minmax(200px, 1fr); gap: 16px; align-items: end; }
        @media (max-width: 720px) { .cnt-filters { grid-template-columns: 1fr; } }
        .cnt-hint { font-size: 12px; line-height: 16px; color: #43474e; margin: 12px 0 0; }
        .cnt-muted { font-size: 12px; color: #74777f; margin: 0; }
        .cnt-gap { height: 16px; }
        .cnt-state { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
        .cnt-summary { display: flex; flex-wrap: wrap; gap: 16px; padding: 8px 0 12px; font-size: 12px; color: #43474e; font-variant-numeric: tabular-nums; }
        .cnt-flat { color: #74777f; }
        .cnt-up { color: #1a6b33; }
        .cnt-down { color: #ba1a1a; }
        .cnt-table-wrap { overflow-x: auto; }
        .cnt-table { width: 100%; border-collapse: collapse; }
        .cnt-table th { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; letter-spacing: 0.02em; text-transform: uppercase; color: #43474e; text-align: left; padding: 8px 12px; border-bottom: 1px solid #c4c6cf; white-space: nowrap; position: sticky; top: 0; background: #f2f4f6; z-index: 1; }
        .cnt-table td { padding: 6px 12px; border-bottom: 1px solid #eceef0; font-size: 12px; color: #191c1e; vertical-align: middle; }
        .cnt-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .cnt-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; }
        .cnt-qty { width: 110px; text-align: right; }
        .cnt-notes { margin-top: 16px; max-width: 560px; }
      `}</style>
    </div>
  );
}
